import { defineStore } from "pinia";
import { computed, ComputedRef, ref, Ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { omit as _omit } from "lodash-es";
import { StartTimeRange } from "@/shared/model/enums/start-time-range";
import usePrematchRouteIds from "../composables/use-prematch-route-ids";
import { usePrematchStore } from "./prematch-sports";
import { PrematchStaticCategories } from "../model/enums/prematch-static-categories";
import { analyticsPageObj } from "@/shared/composables/analytics/use-general-entities";
import { Constants } from "@/shared/model/enums/analytics/constants";

const STORE_NAME = "prematch-filters";

const defaultTimeFilter = null;

const getTimeFilter = (): StartTimeRange | null => {
    const lsFilters = localStorage.getItem(STORE_NAME);
    return lsFilters ? JSON.parse(lsFilters) : defaultTimeFilter;
};

export const usePrematchFilters = defineStore(STORE_NAME, () => {
    const router = useRouter();
    const route = useRoute();
    const { getRouteIds } = usePrematchRouteIds();
    const prematchStore = usePrematchStore();

    const activeTimeFilter: Ref<StartTimeRange | null> = ref(getTimeFilter());

    const activeContentFilter = computed(() => {
        if (getRouteIds.value.leagueId) {
            return prematchStore.leagues?.find((league) => league.id === getRouteIds.value.leagueId)?.name ?? "";
        } else if (getRouteIds.value.countryId) {
            return prematchStore.countries.find((country) => country.id === getRouteIds.value.countryId)?.name ?? "";
        } else if (getRouteIds.value.sportId) {
            if (getRouteIds.value.sportId === PrematchStaticCategories.FAVORITES) {
                return "Oblíbené";
            } else if (getRouteIds.value.sportId === PrematchStaticCategories.MOST_POPULAR) {
                return "Nejsázenější";
            }
            return prematchStore.sports?.find((sport) => sport.id === getRouteIds.value.sportId)?.name ?? "";
        }

        return "";
    });

    const getActiveTimeFilter: ComputedRef<StartTimeRange | null> = computed(() => activeTimeFilter.value);

    function setActiveTimeFilter(filter: StartTimeRange | null): void {
        localStorage.setItem(STORE_NAME, JSON.stringify(filter));
        activeTimeFilter.value = filter;
        const newRouteQuery = {
            ...route.query,
            timeFilter: filter,
        };
        router.replace({
            query: !filter ? _omit(newRouteQuery, "timeFilter") : newRouteQuery,
        });
    }

    function removeActiveTimeFilter(): void {
        localStorage.setItem(STORE_NAME, JSON.stringify(null));
        activeTimeFilter.value = null;
        router.replace({
            query: _omit(route.query, "timeFilter"),
        });

        prematchStore.filteredIds = [];
    }

    function removeContentFilter(): void {
        router.push({ name: "Prematch" });
    }

    function removeAllFilters(): void {
        removeActiveTimeFilter();
        if (!prematchStore.sports?.find((sport) => sport.id === getRouteIds.value.sportId)) {
            removeContentFilter();
        }

        if (getRouteIds.value?.leagueId) {
            const leagueFound = prematchStore.leagues?.find((league) => league.id === getRouteIds.value?.leagueId);
            const leagueEvents = prematchStore.events?.filter(
                (event) => event.drilldownNodes[0].id === getRouteIds.value?.leagueId,
            );
            const leagueOutrights = prematchStore.outrights.filter(
                (outright) => outright.competitionDrilldownTagId === getRouteIds.value?.leagueId,
            );

            if (
                !leagueFound ||
                (leagueFound.eventsAndOutrightsLoaded && !leagueEvents.length && !leagueOutrights.length)
            ) {
                removeContentFilter();
            }
        }
    }

    watch(
        [activeTimeFilter, activeContentFilter],
        ([newTimeFilter, newContentFilter]) => {
            const appliedFilters = [];

            if (newTimeFilter) {
                appliedFilters.push(newTimeFilter);
            }

            if (newContentFilter) {
                appliedFilters.push(newContentFilter);
            }

            analyticsPageObj.appliedFilters =
                appliedFilters.length > 0 ? appliedFilters.join(",") : Constants.NotApplied;
        },
        { immediate: true },
    );

    return {
        activeTimeFilter,
        getActiveTimeFilter,
        activeContentFilter,
        setActiveTimeFilter,
        removeActiveTimeFilter,
        removeContentFilter,
        removeAllFilters,
    };
});
